/* eslint-disable @typescript-eslint/no-empty-function */
import { UntilDestroy } from '@ngneat/until-destroy';
import { ErrorHandlerService } from '@y42/error-handling';
import { Y42FeatureType } from '@y42/json-schema';
import { InterfaceOf } from '@y42/typescript';
import { takePromise } from '@y42/util/take-promise';
import { combineLatest, map, Observable, of } from 'rxjs';
import { JobStatusCode, JobStoreModel } from '../state';
import { AbstractJobService, JobOriginatorData, JobUpdateDetails, RefreshJobsOptions } from './job.service';

@UntilDestroy()
export abstract class AbstractJobAggregationService implements InterfaceOf<AbstractJobService> {
  constructor(
    private readonly jobServices: AbstractJobService[],
    private readonly errorHandlerService: ErrorHandlerService,
  ) {
    if (jobServices.length === 0) {
      throw new Error('Misconfigured job aggregation service. It should contain at least 1 job service');
    }
  }

  readonly defaultFeatureGitPath = '';
  readonly maxDateRange = this.primaryJobService.maxDateRange;
  readonly dateRange$ = this.primaryJobService.dateRange$;
  readonly selectedDateRangeForm = this.primaryJobService.selectedDateRangeForm;
  readonly searchKey$ = this.primaryJobService.searchKey$;
  readonly statusFilter$ = this.primaryJobService.statusFilter$;
  readonly parentType = this.primaryJobService.parentType;
  readonly featureType = this.primaryJobService.featureType;
  readonly loading$ = combineLatest(this.jobServices.map((jobService) => jobService.loading$)).pipe(
    map((loadings) => loadings.reduce((acc, value) => acc || value, false)),
  );
  readonly jobsLoading$ = combineLatest(this.jobServices.map((jobService) => jobService.jobsLoading$)).pipe(
    map((loadings) => loadings.reduce((acc, value) => acc || value, false)),
  );
  readonly jobs$ = combineLatest(this.jobServices.map((jobService) => jobService.jobs$)).pipe(
    map((jobs) => jobs.flat(1)),
  );

  getJob(id: JobStoreModel['request_id'], path: JobStoreModel['path']) {
    const jobService = this.getJobServiceByPath(path);
    if (!jobService) {
      this.errorHandlerService.error('[AbstractJobAggregationService.getJob]: Unsupported path.', {
        context: {
          path,
        },
        toast: `Can't trigger job. Please, try again later.`,
      });
      return Promise.resolve(undefined);
    }
    return jobService.getJob(id, path);
  }

  async fetchJobs(path: string, options: { limit?: number } = {}) {
    const jobService = this.getJobServiceByPath(path);
    if (!jobService) {
      this.errorHandlerService.error('[AbstractJobAggregationService.fetchJobs]: Unsupported path.', {
        context: {
          path,
        },
      });
      return Promise.resolve([]);
    }
    return jobService.fetchJobs(path, options);
  }

  getLatestJobs(options: { featureOrFeatureChildPaths?: string[] } = {}) {
    const jobServiceToPathsMap = new Map<Y42FeatureType, string[]>();
    (options.featureOrFeatureChildPaths ?? []).forEach((path) => {
      const jobService = this.getJobServiceByPath(path);
      if (!jobService) {
        this.errorHandlerService.error('[AbstractJobAggregationService.getLatestJobs]: Unsupported path.', {
          context: {
            path,
          },
        });
        return;
      }
      jobServiceToPathsMap.set(jobService.featureType, [
        ...(jobServiceToPathsMap.get(jobService.featureType) ?? []),
        path,
      ]);
    });

    return takePromise(
      combineLatest(
        this.jobServices.map((jobService) =>
          jobService.getLatestJobs({ featureOrFeatureChildPaths: jobServiceToPathsMap.get(jobService.featureType) }),
        ),
      ).pipe(map((jobs) => jobs.flat(1))),
    );
  }

  triggerJobs(
    paths: Array<JobStoreModel['path']>,
    config: {
      isIncremental: boolean;
    } & JobOriginatorData,
  ) {
    const unknownPaths: string[] = [];
    const aggregatedJobs = new Map<Y42FeatureType, { paths: string[]; jobService: AbstractJobService }>();
    paths.forEach((path) => {
      const jobService = this.getJobServiceByPath(path);
      if (!jobService) {
        unknownPaths.push(path);
      } else {
        const featurePaths = aggregatedJobs.get(jobService.featureType) ?? {
          paths: [],
          jobService,
        };
        featurePaths.paths.push(path);
        aggregatedJobs.set(jobService.featureType, featurePaths);
      }
    });

    if (unknownPaths.length > 0) {
      this.errorHandlerService.error('[AbstractJobAggregationService.triggerJobs]: Unsupported paths.', {
        context: {
          paths: unknownPaths,
        },
      });

      if (unknownPaths.length === paths.length) {
        return of({ jobs: [] });
      }
    }

    return combineLatest(
      [...aggregatedJobs.values()].map((jobs) => jobs.jobService.triggerJobs(jobs.paths, config)),
    ).pipe(
      map((results) => {
        return {
          jobs: results.flatMap((result) => result.jobs),
        };
      }),
    );
  }

  refreshJobs(options: RefreshJobsOptions) {
    return this.jobServices.map((jobService) => jobService.refreshJobs(options));
  }

  cancelJob(id: JobStoreModel['request_id'], path: JobStoreModel['path']): Observable<void> {
    const jobService = this.getJobServiceById(id);
    if (!jobService) {
      return of(undefined);
    }
    return jobService.cancelJob(id, path);
  }

  selectIsJobCanceling(id: JobStoreModel['request_id']): Observable<boolean> {
    const jobService = this.getJobServiceById(id);
    if (!jobService) {
      return of(false);
    }
    return jobService.selectIsJobCanceling(id);
  }

  ngOnDestroy(): void {}

  setActive(id: string | null): void {
    if (id === null) {
      this.jobServices.forEach((jobService) => jobService.setActive(null));
      return;
    }

    const jobService = this.getJobServiceById(id);
    if (!jobService) {
      return;
    }

    jobService.setActive(id);
  }

  setSearchKey(searchKey: string): void {
    this.primaryJobService.setSearchKey(searchKey);
  }

  setStatusFilter(statusFilter: JobStatusCode): void {
    this.primaryJobService.setStatusFilter(statusFilter);
  }

  handleJobUpdate(job: JobUpdateDetails): Promise<void> {
    const jobService = this.getJobServiceByPath(job.path);
    if (!jobService) {
      return Promise.resolve();
    }

    return jobService.handleJobUpdate(job);
  }

  hasJob(id: string): boolean {
    return !!this.getJobServiceById(id);
  }

  private get primaryJobService() {
    return this.jobServices[0]!;
  }

  private getJobServiceByPath(path: string) {
    return this.jobServices.find((jobService) => path.startsWith(jobService.defaultFeatureGitPath));
  }

  private getJobServiceById(id: JobStoreModel['request_id']) {
    return this.jobServices.find((jobService) => jobService.hasJob(id));
  }
}
